/* FTE Report — Summary tab: a month-by-month matrix (Available Hours reference row,
   Existing-Active and TBDs-Future-Hires sections each broken into Employees/IWO
   Employees/Subcontractors/Totals, then a Grand Totals row). The project meta block
   (Project/Name/PM/PC/POP/Contract Type) lives in the shared page chrome (FTEReport.js), next to
   the global filters, not here — it's the same regardless of which tab is active. Table chrome
   (sticky header, fonts, borders) uses the same reportTableConfig.js tokens AOP Budget's
   Summary.js uses. Row set is fixed (not a dynamic list), so unlike AOP Budget's rows there's
   nothing meaningful to column-sort here — the "Columns" control instead lets the user show/hide
   individual months, the same role a Columns picker plays elsewhere in this app
   (IndirectSummaryTab.js's Labor/Non-Labor grids), persisted to localStorage the same way. */
import React, { useEffect, useMemo, useState } from "react";
import Loading from "../../../../common-components/Loading";
import { getFteSummaryAPI } from "../../../../api/reports-api";
import { TABLE_FONT, TH_BG, TH_TEXT, TH_BORDER, TH_ACTUAL_BG, TH_ACTUAL_TEXT, TH_BG_BAND, CELL_FG } from "../../reportTableConfig";
import { monthKeyLabel, fmtNumber, unwrapFteResponse, mapFteSummary } from "./fteReportUtils";
import MonthColumnPicker from "./MonthColumnPicker";

const FONT = TABLE_FONT;
const NAME_W = 220;
const COL_W = 90;
const HIDDEN_MONTHS_KEY = "fteReportHiddenMonths";

const CARD = {
  background: "#fff",
  borderRadius: 12,
  border: "1px solid #EBEBEB",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04),0 4px 16px rgba(0,0,0,0.03)",
  overflow: "hidden",
};

const thStyle = (align, w, isActual) => ({
  position: "sticky",
  top: 0,
  background: isActual ? TH_ACTUAL_BG : TH_BG,
  color: isActual ? TH_ACTUAL_TEXT : TH_TEXT,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  padding: "10px 10px",
  textAlign: align,
  border: "none",
  boxShadow: `inset 0 -1px 0 ${TH_BORDER}`,
  minWidth: w,
  maxWidth: w,
});
const tdName = (bold, indent) => ({
  padding: "8px 12px",
  paddingLeft: indent ? 28 : 12,
  fontSize: 12.5,
  fontWeight: bold ? 700 : 500,
  color: "#111827",
  borderBottom: "1px solid #E2E8F0",
  minWidth: NAME_W,
  maxWidth: NAME_W,
  whiteSpace: "nowrap",
});
const tdVal = (bold, bg) => ({
  padding: "8px 10px",
  fontSize: 12.5,
  fontWeight: bold ? 700 : 400,
  textAlign: "right",
  color: CELL_FG,
  borderBottom: "1px solid #E2E8F0",
  fontVariantNumeric: "tabular-nums",
  minWidth: COL_W,
  maxWidth: COL_W,
  background: bg,
});
const sectionRowStyle = {
  background: TH_BG_BAND,
  color: "#fff",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  padding: "8px 12px",
};

export default function Summary({ filters, dateRange }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [hiddenMonths, setHiddenMonths] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(HIDDEN_MONTHS_KEY) || "[]");
    } catch {
      return [];
    }
  });

  const apiKey = useMemo(() => JSON.stringify({ filters, dateRange }), [filters, dateRange]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getFteSummaryAPI(filters, dateRange)
      .then((res) => {
        if (cancelled) return;
        const body = unwrapFteResponse(res);
        setData(body ? mapFteSummary(body) : null);
        if (!body) setError(res?.message || "Failed to load data");
      })
      .catch(() => {
        if (cancelled) return;
        setData(null);
        setError("Failed to load data");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  const toggleMonth = (key) => {
    setHiddenMonths((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      localStorage.setItem(HIDDEN_MONTHS_KEY, JSON.stringify(next));
      return next;
    });
  };
  const resetMonths = () => {
    setHiddenMonths([]);
    localStorage.setItem(HIDDEN_MONTHS_KEY, "[]");
  };

  if (loading) return <Loading />;
  if (error) {
    return (
      <div style={{ padding: "40px 24px", textAlign: "center", color: "#D64550", fontSize: 13, fontFamily: FONT }}>
        {error}
      </div>
    );
  }
  if (!data) return null;

  const allMonths = data.monthKeys || [];
  const visibleMonths = allMonths.filter((k) => !hiddenMonths.includes(k));
  const colCount = visibleMonths.length + 2; // name col + averages col

  const sectionRow = (line, label, indent = true) => (
    <tr key={label}>
      <td style={tdName(false, indent)}>{label}</td>
      {visibleMonths.map((m) => (
        <td key={m} style={tdVal(false)}>{fmtNumber(line?.months?.[m] ?? 0)}</td>
      ))}
      <td style={tdVal(false, "#F8FAFC")}>{fmtNumber(line?.average ?? 0)}</td>
    </tr>
  );

  const totalsRow = (line, label, bg) => (
    <tr key={label}>
      <td style={{ ...tdName(true, true), background: bg }}>{label}</td>
      {visibleMonths.map((m) => (
        <td key={m} style={tdVal(true, bg)}>{fmtNumber(line?.months?.[m] ?? 0)}</td>
      ))}
      <td style={tdVal(true, bg)}>{fmtNumber(line?.average ?? 0)}</td>
    </tr>
  );

  return (
    <div style={{ fontFamily: FONT, paddingBottom: 24 }}>
      <div style={CARD}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>FTE Report with Timephased Hiring Needs</span>
          <MonthColumnPicker monthKeys={allMonths} hidden={hiddenMonths} onToggle={toggleMonth} onReset={resetMonths} />
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: NAME_W + colCount * COL_W, fontFamily: FONT }}>
            <thead>
              <tr>
                <th style={thStyle("left", NAME_W)} />
                {visibleMonths.map((m) => (
                  <th key={m} style={thStyle("right", COL_W, data.actualMonths.includes(m))}>{monthKeyLabel(m)}</th>
                ))}
                <th style={thStyle("right", COL_W)}>Average</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ ...tdName(false, false), fontStyle: "italic", color: "#6B7280" }}>Available Hours</td>
                {visibleMonths.map((m) => (
                  <td key={m} style={{ ...tdVal(false), fontStyle: "italic", color: "#6B7280" }}>
                    {fmtNumber(data.availableHours?.[m])}
                  </td>
                ))}
                <td style={{ ...tdVal(false, "#F8FAFC"), fontStyle: "italic", color: "#6B7280" }}>
                  {fmtNumber(data.availableHoursAverage)}
                </td>
              </tr>

              <tr><td colSpan={colCount} style={sectionRowStyle}>Existing, Active</td></tr>
              {sectionRow(data.existing.employees, "Employees")}
              {sectionRow(data.existing.iwo, "IWO Employees")}
              {sectionRow(data.existing.subcontractors, "Subcontractors")}
              {totalsRow(data.existing.totals, "Totals", "#F8FAFC")}

              <tr><td colSpan={colCount} style={sectionRowStyle}>TBDs, Future Hires</td></tr>
              {sectionRow(data.tbd.employees, "Employees")}
              {sectionRow(data.tbd.iwo, "IWO Employees")}
              {sectionRow(data.tbd.subcontractors, "Subcontractors")}
              {totalsRow(data.tbd.totals, "Totals", "#F8FAFC")}

              <tr>
                <td style={{ ...tdName(true, false), background: "#F0FFF4" }}>Grand Totals</td>
                {visibleMonths.map((m) => (
                  <td key={m} style={tdVal(true, "#F0FFF4")}>{fmtNumber(data.grandTotals?.months?.[m] ?? 0)}</td>
                ))}
                <td style={tdVal(true, "#F0FFF4")}>{fmtNumber(data.grandTotals?.average ?? 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
